import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import {
  type BooleanResponse,
  type Paginate,
  type SearchRequest,
} from "@/types";

import { type AdminReviewPeriod } from "../types";

export type ReviewPeriodList = Paginate<
  AdminReviewPeriod<true, true, true, true>
>;

export const reviewPeriods = createQueryKeys(
  "admin-performance-review-periods",
  {
    all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
    one: (id?: number) => [id],
  },
);

const include = "objectivePeriod,participants,reviewPeriodTypes,units";

export const fetchReviewPeriodsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...reviewPeriods.all(page, search),
    queryFn: () =>
      adminApi
        .get<ReviewPeriodList>(`/performance/review-periods`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchReviewPeriods = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchReviewPeriodsQueryOptions(page, search));

export const fetchReviewPeriodQueryOptions = (id?: number) =>
  queryOptions({
    ...reviewPeriods.one(id),
    queryFn: () =>
      adminApi
        .get<AdminReviewPeriod<true, true, true>>(
          `/performance/review-periods/${id}`,
          {
            params: { include },
          },
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchReviewPeriod = (id?: number) =>
  useQuery(fetchReviewPeriodQueryOptions(id));

export interface ReviewPeriodRequest {
  name: string;
  is_company_wide?: boolean | null;
  unit_ids: number[];
  start_date: string;
  end_date: string;
  performance_objective_period_id?: number | null;
  rating_step?: string | null;
  review_period_rating_labels?: string[] | null;
  has_rating?: boolean;
  review_period_types: {
    type: string;
    start_date: string;
    end_date: string;
    performance_review_form_id?: number | null;
    question?: string | null;
    weight?: string | null;
  }[];
  participants: number[];
  review_period_assignments?: string | null;
  can_add_manager_peer?: boolean;
  can_add_peer?: boolean;
  min_peer_reviewers_count?: number;
  min_manager_peer_reviewers_count?: number;
  hide_manager_review: boolean;
  hide_peer_review: boolean;
  lock_snapshot_after_review_start?: boolean;
  hide_peer_names: boolean;
  scoring_enable?: boolean;
}

export const useCreateReviewPeriod = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ReviewPeriodRequest) =>
      adminApi
        .post("/performance/review-periods/", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewPeriods.all._def,
      });
    },
  });
};

export interface UpdateReviewPeriodRequest {
  name: string;
  participants: number[];
}

export const useUpdateReviewPeriod = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: UpdateReviewPeriodRequest) =>
      adminApi
        .put(`/performance/review-periods/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewPeriods.all._def,
      });
    },
  });
};

export interface UpdateReviewPeriodTypesRequest {
  review_period_types: {
    type: string;
    performance_review_form_id?: number | null;
    question?: string | null;
    weight?: string | null;
    has_rating?: boolean;
  }[];
  rating_step?: string | null;
  review_period_rating_labels?: string[] | null;
}

export const useUpdateReviewPeriodQuestions = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: UpdateReviewPeriodTypesRequest) =>
      adminApi
        .put(`/performance/review-periods/${id}/questions`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewPeriods.all._def,
      });
    },
  });
};

export interface UpdateReviewPeriodDatesRequest {
  start_date: string;
  end_date: string;
  review_period_types: {
    type: string;
    start_date: string;
    end_date: string;
  }[];
}

export const useUpdateReviewPeriodDates = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: UpdateReviewPeriodDatesRequest) =>
      adminApi
        .put(`/performance/review-periods/${id}/dates`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewPeriods.all._def,
      });
    },
  });
};

export const useDeleteReviewPeriod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      adminApi.delete(`performance/review-periods/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: reviewPeriods.all._def,
      });
      queryClient.invalidateQueries(reviewPeriods.one(id));
    },
  });
};

export const useDisableReviewPeriod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      adminApi.put(`performance/review-periods/${id}/disable`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: reviewPeriods.all._def,
      });
      queryClient.invalidateQueries(reviewPeriods.one(id));
    },
  });
};

export const useDownloadReviewPeriodReport = () =>
  useMutation({
    mutationFn: (id: number) =>
      adminApi.get<Blob>(`/performance/review-periods/${id}/export`, {
        responseType: "blob",
      }),
  });

export const useValidateReviewPeriodAssignments = () => {
  return useMutationWithError({
    mutationFn: (data: { file: string }) =>
      adminApi
        .post<
          BooleanResponse<{ error_file?: string }>
        >("performance/review-period-assignments/validate", data)
        .then((response) => response.data),
  });
};

export interface UpdateMinPeerReviewersCountRequest {
  min_peer_reviewers_count: number;
  min_manager_peer_reviewers_count: number;
}

export const useUpdateMinPeerReviewersCount = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: UpdateMinPeerReviewersCountRequest) =>
      adminApi
        .put(`/performance/review-periods/${id}/min-peer-reviewers-count`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewPeriods.all._def,
      });
    },
  });
};
